// iter150-review — Shared unread-count store (/api/inbox/unread-count)
// used by BottomNav, mobile AppHeader bell, and desktop InboxBell. See
// useUnreadCount.js for the role filtering + visibility+online gating.
import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { refreshUnreadNow } from "@/lib/useUnreadCount";
import { Send, Inbox as InboxIcon, ChevronLeft, MessageSquare, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function timeAgo(iso) {
  try {
    return new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
}

function MessageBubble({ m }) {
  const fromAdmin = m.fromRole === "admin";
  return (
    <div className={`flex ${fromAdmin ? "justify-start" : "justify-end"}`} data-testid={`inbox-msg-${m.id}`}>
      <div className={`max-w-[78%] rounded-2xl px-4 py-2.5 text-sm shadow-sm ${fromAdmin ? "rounded-tl-sm bg-white text-ink-900 border border-border" : "rounded-tr-sm bg-clay text-white"}`}>
        <div className={`mb-0.5 text-[0.65rem] font-semibold uppercase tracking-wide ${fromAdmin ? "text-clay" : "text-white/85"}`}>
          {fromAdmin ? "Admin" : "You"} · {timeAgo(m.createdAt)}
        </div>
        <p className="whitespace-pre-wrap leading-relaxed">{m.body}</p>
      </div>
    </div>
  );
}

export default function SchoolInbox() {
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const refreshThreads = async () => {
    try {
      const r = await axios.get(`${API}/inbox/threads`);
      setThreads(r.data || []);
      // iter150-review — immediately refresh the shared badge too, so
      // nav bells / BottomNav agree with what the user sees here.
      refreshUnreadNow();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load inbox");
    }
  };

  const openThread = async (id) => {
    setActiveId(id);
    setLoading(true);
    try {
      const r = await axios.get(`${API}/inbox/threads/${id}`);
      setActive(r.data.thread);
      setMessages(r.data.messages || []);
      setTimeout(() => scrollRef.current?.scrollTo({ top: 1e7, behavior: "smooth" }), 60);
      // refresh side-list so unread badges update
      refreshThreads();
      // iter150-review — school opened the thread, server dropped
      // unreadSchool on it; refresh the shared badge immediately.
      refreshUnreadNow();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to open thread");
    } finally { setLoading(false); }
  };

  const sendReply = async () => {
    const body = reply.trim();
    if (!body || !activeId) return;
    setSending(true);
    try {
      await axios.post(`${API}/inbox/threads/${activeId}/reply`, { body });
      setReply("");
      await openThread(activeId);
      toast.success("Reply sent");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reply failed");
    } finally { setSending(false); }
  };

  useEffect(() => { refreshThreads(); }, []);

  return (
    <main className="min-h-screen bg-sand-50 text-ink-900" data-testid="school-inbox-page">
      <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6">
        <header className="mb-4 flex items-center justify-between gap-2">
          <div>
            <p className="text-[0.7rem] font-semibold uppercase tracking-[0.22em] text-clay">Inbox</p>
            <h1 className="font-heading text-2xl font-bold text-ink-900">Messages from Admin</h1>
            <p className="text-xs text-ink-700">Read announcements and reply to admin queries.</p>
          </div>
          <Button size="sm" variant="outline" onClick={() => { refreshThreads(); if (activeId) openThread(activeId); }} data-testid="inbox-refresh-btn" className="gap-1.5">
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </Button>
        </header>

        {threads.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-border bg-white p-10 text-center" data-testid="inbox-empty">
            <InboxIcon className="mx-auto h-10 w-10 text-ink-500" />
            <p className="mt-3 text-sm font-semibold text-ink-900">No messages yet</p>
            <p className="mt-1 text-xs text-ink-700">When the admin sends you a message, it will appear here.</p>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-[260px_1fr]">
            {/* Thread list */}
            <aside className={`rounded-2xl border border-border bg-white p-2 ${activeId ? "hidden md:block" : ""}`} data-testid="inbox-thread-list">
              {threads.map((t) => (
                <button
                  type="button"
                  key={t.id}
                  onClick={() => openThread(t.id)}
                  data-testid={`inbox-thread-${t.id}`}
                  className={`flex w-full items-start gap-2 rounded-xl px-3 py-2.5 text-left transition ${activeId === t.id ? "bg-clay/10" : "hover:bg-sand-50"}`}
                >
                  <MessageSquare className="mt-0.5 h-4 w-4 shrink-0 text-clay" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="truncate text-sm font-semibold text-ink-900">{t.subject || "Message"}</p>
                      {t.unreadSchool > 0 && (
                        <span className="grid h-5 min-w-[20px] place-items-center rounded-full bg-clay px-1.5 text-[0.65rem] font-bold text-white">{t.unreadSchool}</span>
                      )}
                    </div>
                    <p className="mt-0.5 truncate text-[0.7rem] text-ink-700">{t.lastBody || "—"}</p>
                    <p className="mt-0.5 text-[0.6rem] text-ink-700">{timeAgo(t.lastAt)}</p>
                  </div>
                </button>
              ))}
            </aside>

            {/* Thread view */}
            <section className={`flex min-h-[420px] flex-col rounded-2xl border border-border bg-white ${!activeId ? "hidden md:flex md:items-center md:justify-center" : ""}`}>
              {!active ? (
                <p className="text-sm text-ink-700">Select a message to read it.</p>
              ) : (
                <>
                  <div className="flex items-center gap-2 border-b border-border px-4 py-3">
                    <button className="grid h-8 w-8 place-items-center rounded-full hover:bg-sand-50 md:hidden" onClick={() => setActiveId(null)} data-testid="inbox-back-btn">
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-semibold text-ink-900">{active.subject}</p>
                      <p className="text-[0.7rem] text-ink-700">Started {timeAgo(active.createdAt)}</p>
                    </div>
                  </div>

                  <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto bg-sand-50 px-4 py-4" data-testid="inbox-thread-body" style={{ maxHeight: "60vh" }}>
                    {loading && <p className="text-xs text-ink-700">Loading…</p>}
                    {messages.map((m) => <MessageBubble key={m.id} m={m} />)}
                  </div>

                  <div className="flex items-end gap-2 border-t border-border bg-white px-3 py-3">
                    <textarea
                      data-testid="inbox-reply-input"
                      value={reply}
                      onChange={(e) => setReply(e.target.value)}
                      placeholder="Write your reply…  ଆପଣଙ୍କ ଉତ୍ତର ଲେଖନ୍ତୁ…"
                      rows={2}
                      className="block flex-1 resize-none rounded-xl border border-border bg-sand-50 px-3 py-2 text-sm outline-none focus:border-clay focus:ring-1 focus:ring-clay/30"
                    />
                    <Button onClick={sendReply} disabled={sending || !reply.trim()} data-testid="inbox-send-reply-btn" className="gap-1.5 bg-clay text-white hover:bg-clay-dark">
                      <Send className="h-4 w-4" /> Send
                    </Button>
                  </div>
                </>
              )}
            </section>
          </div>
        )}
      </div>
    </main>
  );
}
